/**
 * CV Management Component - View and manage uploaded CV
 *
 * Copyright (c) 2024 Technical Interviewer Attempt. All Rights Reserved.
 * Proprietary and Confidential. Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useState, useCallback, useEffect } from 'react';
import CVUpload from './CVUpload';

// For CV RAG operations, always use the deployed API (Pinecone is configured there)
// This ensures RAG operations work even during local development
const API_URL =
  import.meta.env.VITE_RAG_API_URL ||
  import.meta.env.VITE_API_URL ||
  'https://krow7dgex4.execute-api.eu-west-2.amazonaws.com';

interface CVManagementProps {
  user: { username?: string; email?: string };
  getToken: () => string | null;
  onBack?: () => void;
}

interface CVStatus {
  has_cv: boolean;
  vector_count?: number;
  index_name?: string;
  message?: string;
}

const CVManagement: React.FC<CVManagementProps> = ({
  user,
  getToken,
  onBack,
}) => {
  const [cvStatus, setCvStatus] = useState<CVStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState<boolean>(false);

  const username = user?.username || user?.email?.split('@')[0] || 'unknown';

  const fetchCVStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${API_URL}/cv/status?username=${encodeURIComponent(username)}`
      );
      if (response.ok) {
        const data = await response.json();
        setCvStatus(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || 'Failed to check CV status');
      }
    } catch (err) {
      console.error('Error fetching CV status:', err);
      setError('Failed to check CV status. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchCVStatus();
  }, [fetchCVStatus]);

  const handleUploadSuccess = () => {
    setShowUpload(false);
    fetchCVStatus();
  };

  return (
    <div className="min-h-screen p-4 sm:p-5 bg-slate-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl text-slate-800 font-bold mb-2">
              📄 CV Management
            </h1>
            <p className="text-slate-500 text-sm sm:text-base">
              Manage your CV for CV Review interviews
            </p>
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg border-none cursor-pointer font-semibold transition-all duration-200 hover:bg-slate-300"
            >
              ← Back
            </button>
          )}
        </div>

        {/* CV Status Card */}
        <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-xl border border-slate-200 mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4">
            CV Status
          </h2>

          {loading ? (
            <div className="flex items-center gap-3 text-slate-600">
              <div className="w-5 h-5 border-2 border-slate-300 border-t-indigo-500 rounded-full animate-spin"></div>
              <span>Loading CV status...</span>
            </div>
          ) : error ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm m-0">{error}</p>
            </div>
          ) : cvStatus ? (
            <div>
              {cvStatus.has_cv ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <span className="text-green-600 text-2xl">✓</span>
                    <div className="flex-1">
                      <h3 className="text-green-800 font-semibold text-base sm:text-lg m-0 mb-2">
                        CV Uploaded and Ready
                      </h3>
                      <div className="space-y-1 text-sm text-green-700">
                        {cvStatus.vector_count !== undefined && (
                          <p className="m-0">
                            <strong>Sections indexed:</strong>{' '}
                            {cvStatus.vector_count}
                          </p>
                        )}
                        {cvStatus.index_name && (
                          <p className="m-0">
                            <strong>Index:</strong> {cvStatus.index_name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <span className="text-slate-400 text-2xl">○</span>
                    <div className="flex-1">
                      <h3 className="text-slate-800 font-semibold text-base sm:text-lg m-0 mb-2">
                        No CV Uploaded
                      </h3>
                      <p className="text-slate-600 text-sm m-0">
                        {cvStatus.message ||
                          'Upload your CV as text to enable CV Review interviews. Your CV will be used to ask personalized questions based on your experience and skills.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Actions */}
        <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-xl border border-slate-200">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4">
            Actions
          </h2>

          <div className="space-y-3">
            <button
              onClick={() => setShowUpload(true)}
              className="w-full px-4 py-3 sm:py-4 text-base sm:text-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg border-none cursor-pointer font-semibold transition-all duration-200 hover:from-indigo-600 hover:to-purple-700 hover:-translate-y-0.5 hover:shadow-xl"
            >
              {cvStatus?.has_cv ? '📄 Update CV' : '📤 Upload CV'}
            </button>

            {cvStatus?.has_cv && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-blue-800 font-semibold text-sm sm:text-base mb-2">
                  About CV Review Interviews
                </h3>
                <ul className="text-blue-700 text-xs sm:text-sm space-y-1 list-disc list-inside">
                  <li>
                    Your CV will be used to ask personalized technical and
                    behavioral questions
                  </li>
                  <li>
                    Questions will reference your experience, skills, and
                    projects
                  </li>
                  <li>
                    Upload a new CV to replace your existing one (old CV will be
                    deleted)
                  </li>
                  <li>CV is processed and indexed automatically upon upload</li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <h3 className="text-indigo-800 font-semibold text-sm sm:text-base mb-2">
            💡 Tips for CV Upload
          </h3>
          <ul className="text-indigo-700 text-xs sm:text-sm space-y-1 list-disc list-inside">
            <li>Paste your CV content as plain text</li>
            <li>Include all relevant work experience, skills, and projects</li>
            <li>Mention technologies, frameworks, and tools you've used</li>
            <li>Include educational background and certifications</li>
            <li>Copy and paste directly from your CV document</li>
            <li>
              The more detailed your CV, the better the interview questions will
              be
            </li>
          </ul>
        </div>
      </div>

      {/* CV Upload Modal */}
      {showUpload && (
        <CVUpload
          user={user}
          getToken={getToken}
          onClose={() => setShowUpload(false)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
};

export default CVManagement;
